/**
 * Utility functions for DenaliActionPipeline dynamic forms
 */

import { query } from 'json-p3/dist/json-p3.esm'

/**
 * Build react-hook-form validation rules from field definition
 * Uses extended format with value and message properties
 */
export const buildValidationRules = (field) => {
  const rules = {}

  // Handle required validation
  if (field.validations?.required || field.required) {
    rules.required = {
      value: true,
      message: 'This field is required.'
    }
  }

  // Handle validation rules
  if (field.validationRules) {
    const { min, max, pattern, minLength, maxLength } = field.validationRules

    if (min !== undefined) {
      rules.min = {
        value: min,
        message: `Minimum value is ${min}.`
      }
    }

    if (max !== undefined) {
      rules.max = {
        value: max,
        message: `Maximum value is ${max}.`
      }
    }

    if (minLength !== undefined) {
      rules.minLength = {
        value: minLength,
        message: `Minimum length is ${minLength} characters.`
      }
    }

    if (maxLength !== undefined) {
      rules.maxLength = {
        value: maxLength,
        message: `Maximum length is ${maxLength} characters.`
      }
    }

    if (pattern) {
      rules.pattern = {
        value: new RegExp(pattern),
        message: 'Invalid format.'
      }
    }
  }

  return rules
}

/**
 * Get default value for a field based on its editor type
 */
export const getDefaultValue = (field) => {
  // Use explicit default if provided
  if (field.defaultValue !== undefined) {
    return field.defaultValue
  }

  // Otherwise infer from editor type
  switch (field.editorType) {
    case 'bool':
      return false
    case 'number':
      return null
    case 'json':
      return null
    case 'select':
      return field.multiple ? [] : null
    case 'text':
    case 'textarea':
    default:
      return ''
  }
}

/**
 * Resolve JSON path expression against data
 * Returns resolved value or null if not found
 */
export const resolveJSONPath = (data, path) => {
  if (!data || !path) {
    return null
  }

  try {
    // json-p3 query syntax: query(path, data)
    const queryResult = query(path, data)
    // Results are objects with { value, location, root }
    return queryResult.nodes[0]?.value ?? null
  } catch (error) {
    console.warn('Failed to resolve JSON path:', path, error)
    return null
  }
}

/**
 * Build default values object for react-hook-form from field groups
 * Resolves initFrom paths if initializationData is provided
 */
export const buildDefaultValues = (groups, initializationData) => {
  const defaultValues = {}

  if (!groups || !Array.isArray(groups)) {
    return defaultValues
  }

  groups.forEach(group => {
    if (group.fields && Array.isArray(group.fields)) {
      group.fields.forEach(field => {
        // Try initFrom first if available
        if (field.initFrom && initializationData) {
          const resolvedValue = resolveJSONPath(initializationData, field.initFrom)
          if (resolvedValue !== null && resolvedValue !== undefined) {
            defaultValues[field.id] = resolvedValue
            return
          }
        }

        // Fall back to default value
        defaultValues[field.id] = getDefaultValue(field)
      })
    }
  })

  return defaultValues
}

/**
 * Sort items by ordinal property
 */
export const sortByOrdinal = (items) => {
  if (!items || !Array.isArray(items)) {
    return []
  }

  return [...items].sort((a, b) => {
    const ordinalA = a.ordinal ?? Number.MAX_SAFE_INTEGER
    const ordinalB = b.ordinal ?? Number.MAX_SAFE_INTEGER
    return ordinalA - ordinalB
  })
}

/**
 * Parse JSON string safely
 */
export const parseJSON = (value) => {
  if (!value || typeof value !== 'string') {
    return null
  }

  try {
    return JSON.parse(value)
  } catch (error) {
    return null
  }
}

/**
 * Stringify JSON safely with formatting
 */
export const stringifyJSON = (value) => {
  if (value === null || value === undefined) {
    return ''
  }

  try {
    return JSON.stringify(value, null, 2)
  } catch (error) {
    return String(value)
  }
}

/**
 * Coerce form values to appropriate types for submission
 */
export const coerceFormValues = (values, fields) => {
  const coerced = { ...values }

  if (!fields || !Array.isArray(fields)) {
    return coerced
  }

  fields.forEach(field => {
    const value = coerced[field.id]

    switch (field.editorType) {
      case 'number':
        if (value !== null && value !== '') {
          coerced[field.id] = Number(value)
        }
        break
      case 'bool':
        coerced[field.id] = Boolean(value)
        break
      case 'json':
        if (typeof value === 'string') {
          coerced[field.id] = parseJSON(value)
        }
        break
      default:
        // Keep as is
        break
    }
  })

  return coerced
}

/**
 * Get all fields from groups array
 */
export const getAllFields = (groups) => {
  if (!groups || !Array.isArray(groups)) {
    return []
  }

  return groups.reduce((acc, group) => {
    if (group.fields && Array.isArray(group.fields)) {
      return [...acc, ...group.fields]
    }
    return acc
  }, [])
}

/**
 * Build args object by resolving argMapping paths against form data
 * @param {Object} formData - The submitted form data
 * @param {Object} argMapping - Mapping of arg names to JSON paths
 * @returns {Object} Resolved args object
 */
export const buildArgsFromMapping = (formData, argMapping) => {
  if (!argMapping || typeof argMapping !== 'object') {
    return {}
  }

  const args = {}

  Object.entries(argMapping).forEach(([argName, path]) => {
    const value = resolveJSONPath(formData, path)
    if (value !== null && value !== undefined) {
      args[argName] = value
    }
  })

  return args
}
